/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, ErrorText, Field, Input, Modal, PageHeader, Select, Tabs, Textarea } from '@/components/ui';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useDirectory } from '@/lib/hooks';

type Tab = 'conv' | 'kb' | 'settings';
const CH: Record<string, string> = { WEB: 'сайт', PORTAL: 'кабинет', APP: 'приложение', MAX: 'MAX', VK: 'ВКонтакте', TELEGRAM: 'Telegram' };
const STATUS: Record<string, [string, 'slate' | 'amber' | 'green']> = { BOT: ['отвечает бот', 'green'], HANDOFF: ['ждёт сотрудника', 'amber'], CLOSED: ['закрыт', 'slate'] };

/** M22 Чат-бот первичной консультации: диалоги, база знаний, настройки и код виджета для сайта */
export function BotPage() {
  const [tab, setTab] = useState<Tab>('conv');
  return (
    <>
      <PageHeader title="Чат-бот" subtitle="Первичная консультация на сайте и в личном кабинете, передача сотруднику, создание сделки" />
      <Tabs tabs={[{ id: 'conv', label: 'Диалоги' }, { id: 'kb', label: 'База знаний' }, { id: 'settings', label: 'Настройки и виджет' }]} value={tab} onChange={setTab} />
      <div className="mt-4">{tab === 'conv' ? <Conversations /> : tab === 'kb' ? <Kb /> : <Settings />}</div>
    </>
  );
}

function Conversations() {
  const qc = useQueryClient();
  const [status, setStatus] = useState('HANDOFF');
  const [sel, setSel] = useState<string | null>(null);
  const [text, setText] = useState('');
  const { data } = useQuery({ queryKey: ['bot-conv', status], queryFn: () => api.get<any[]>(`/bot/conversations${status ? `?status=${status}` : ''}`), refetchInterval: 15_000 });
  const c = data?.find((x) => x.id === sel);
  const reply = useMutation({ mutationFn: (b: any) => api.post(`/bot/conversations/${sel}/reply`, b), onSuccess: () => { setText(''); qc.invalidateQueries({ queryKey: ['bot-conv'] }); } });
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <div className="card p-3">
        <Select value={status} onChange={(e) => setStatus(e.target.value)} options={[{ value: 'HANDOFF', label: 'Ждут сотрудника' }, { value: 'BOT', label: 'Отвечает бот' }, { value: 'CLOSED', label: 'Закрытые' }, { value: '', label: 'Все' }]} />
        <ul className="mt-2 divide-y divide-slate-100 text-sm">
          {(data ?? []).map((x) => (
            <li key={x.id} onClick={() => setSel(x.id)} className={`cursor-pointer px-1 py-2 ${sel === x.id ? 'bg-brand-50' : 'hover:bg-slate-50'}`}>
              <div className="flex items-center justify-between gap-2"><span className="truncate">{x.contact?.name ?? x.contact?.phone ?? 'Посетитель'}</span><Badge>{CH[x.channel] ?? x.channel}</Badge></div>
              <div className="truncate text-xs text-slate-500">{(x.messages as any[]).at(-1)?.text}</div>
            </li>
          ))}
          {!data?.length && <li className="py-2 text-slate-500">Нет диалогов</li>}
        </ul>
      </div>
      <div className="card p-4 lg:col-span-2">
        {!c ? <div className="text-sm text-slate-500">Выберите диалог</div> : <>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Badge tone={STATUS[c.status]?.[1]}>{STATUS[c.status]?.[0]}</Badge>
            {c.handoffReason && <span className="text-xs text-slate-500">причина передачи: {c.handoffReason}</span>}
            {c.partyId && <Link className="text-sm text-brand-700 hover:underline" to={`/parties/${c.partyId}`}>карточка лица</Link>}
            {c.dealId && <Link className="text-sm text-brand-700 hover:underline" to="/deals">сделка создана</Link>}
          </div>
          <div className="max-h-[50vh] space-y-2 overflow-y-auto rounded bg-slate-50 p-3">
            {(c.messages as any[]).map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[80%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${m.role === 'user' ? 'bg-white' : m.role === 'staff' ? 'bg-brand-600 text-white' : 'bg-brand-100'}`}>{m.text}<div className="mt-1 text-[10px] opacity-60">{m.role === 'user' ? 'посетитель' : m.role === 'staff' ? 'сотрудник' : 'бот'}</div></div>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-2">
            <Textarea rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder="Ответ сотрудника" />
            <div className="flex flex-wrap gap-2">
              <Button disabled={!text.trim() || reply.isPending} onClick={() => reply.mutate({ text, status: 'HANDOFF' })}>Ответить</Button>
              {c.status !== 'BOT' && <Button variant="secondary" onClick={() => reply.mutate({ status: 'BOT' })}>Вернуть боту</Button>}
              {c.status !== 'CLOSED' && <Button variant="ghost" onClick={() => reply.mutate({ status: 'CLOSED' })}>Закрыть</Button>}
            </div>
            <ErrorText error={reply.error} />
          </div>
        </>}
      </div>
    </div>
  );
}

function Kb() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [edit, setEdit] = useState<any | null>(null);
  const { data } = useQuery({ queryKey: ['bot-kb'], queryFn: () => api.get<any[]>('/bot/kb') });
  const inv = () => qc.invalidateQueries({ queryKey: ['bot-kb'] });
  const tpl = useMutation({ mutationFn: () => api.post('/bot/kb/template', {}), onSuccess: inv });
  const save = useMutation({ mutationFn: (b: any) => (b.id ? api.patch(`/bot/kb/${b.id}`, { title: b.title, body: b.body, active: b.active }) : api.post('/bot/kb', { title: b.title, body: b.body, active: true })), onSuccess: () => { setEdit(null); inv(); } });
  const del = useMutation({ mutationFn: (id: string) => api.del(`/bot/kb/${id}`), onSuccess: inv });
  const w = can('deal.write');
  return (
    <div className="card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-500">Бот отвечает только по этим статьям, о стоимости и сроках говорит то, что здесь указано. Остальное передаёт сотруднику.</div>
        {w && <div className="flex gap-2">{!data?.length && <Button variant="secondary" onClick={() => tpl.mutate()}>Заполнить шаблоном</Button>}<Button onClick={() => setEdit({ title: '', body: '' })}>Добавить статью</Button></div>}
      </div>
      <ul className="divide-y divide-slate-100">
        {(data ?? []).map((k) => (
          <li key={k.id} className="flex items-start justify-between gap-3 py-2 text-sm">
            <div><div className="font-medium">{k.title} {!k.active && <Badge>выключена</Badge>}</div><div className="line-clamp-2 text-slate-600">{k.body}</div></div>
            {w && <div className="flex shrink-0 gap-1"><Button size="sm" variant="ghost" onClick={() => setEdit(k)}>Изменить</Button><Button size="sm" variant="ghost" onClick={() => del.mutate(k.id)}>Удалить</Button></div>}
          </li>
        ))}
        {!data?.length && <li className="py-2 text-sm text-slate-500">База знаний пуста</li>}
      </ul>
      <Modal title={edit?.id ? 'Статья базы знаний' : 'Новая статья'} open={!!edit} onClose={() => setEdit(null)} wide>
        {edit && <div className="space-y-3">
          <Field label="Тема"><Input value={edit.title} onChange={(e) => setEdit({ ...edit, title: e.target.value })} /></Field>
          <Field label="Текст"><Textarea rows={10} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })} /></Field>
          {edit.id && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} />Использовать в ответах</label>}
          <ErrorText error={save.error} />
          <Button disabled={edit.title.length < 2 || edit.body.length < 2 || save.isPending} onClick={() => save.mutate(edit)}>Сохранить</Button>
        </div>}
      </Modal>
    </div>
  );
}

function Settings() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const { data: users } = useDirectory();
  const { data } = useQuery({ queryKey: ['bot-settings'], queryFn: () => api.get<any>('/bot/settings') });
  const [f, setF] = useState({ company: '', greeting: '', ownerId: '' });
  useEffect(() => { if (data) setF({ company: data.company ?? '', greeting: data.greeting ?? '', ownerId: data.ownerId ?? '' }); }, [data]);
  const save = useMutation({ mutationFn: () => api.patch('/bot/settings', { company: f.company, greeting: f.greeting, ownerId: f.ownerId || null }), onSuccess: () => qc.invalidateQueries({ queryKey: ['bot-settings'] }) });
  const snippet = `<script src="${location.origin}/api/v1/public/bot/widget.js" defer></script>`;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="card space-y-3 p-4">
        <Field label="Название организации в ответах"><Input value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} /></Field>
        <Field label="Приветствие"><Input value={f.greeting} onChange={(e) => setF({ ...f, greeting: e.target.value })} /></Field>
        <Field label="Ответственный за новые обращения" hint="Ему ставится задача, когда посетитель оставил телефон или просит сотрудника"><Select placeholder="не выбран" value={f.ownerId} onChange={(e) => setF({ ...f, ownerId: e.target.value })} options={(users ?? []).map((u) => ({ value: u.id, label: u.fullName }))} /></Field>
        <ErrorText error={save.error} />
        {can('user.manage') && <Button disabled={save.isPending} onClick={() => save.mutate()}>Сохранить</Button>}
      </div>
      <div className="card space-y-2 p-4 text-sm">
        <div className="font-medium">Виджет для сайта</div>
        <div className="text-slate-600">Вставьте строку перед закрывающим тегом &lt;/body&gt; на сайте. Адрес CRM должен быть доступен из интернета по HTTPS.</div>
        <pre className="overflow-x-auto rounded bg-slate-900 p-3 text-xs text-white">{snippet}</pre>
        <Button size="sm" variant="secondary" onClick={() => navigator.clipboard.writeText(snippet)}>Скопировать</Button>
        <div className="text-xs text-slate-500">В виджете посетитель видит уведомление об обработке персональных данных. Разместите на сайте политику обработки персональных данных (152-ФЗ).</div>
      </div>
    </div>
  );
}
